import {Box, Typography} from '@mui/material';

import {ProfileTabButton} from './ProfileTabButton';
import {WorkforceProfileAlignmentItemsOverview} from './WorkforceProfileAlignmentItemsOverview';
import type {SkillScoreDimension} from './WorkforceProfileSkillScoreBreakdown';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import type {WorkforceProfileCompetencyRow} from '../../types/WorkforceProfileTypes';

export interface SkillSectionConfig {
  key: SkillScoreDimension;
  label: string;
  title: string;
  available: boolean;
  rows: WorkforceProfileCompetencyRow[];
  newlyMetKeys?: Set<string>;
  newlyMetNote?: string;
  emptyExplanation?: string | null;
}

export interface Props {
  sections: SkillSectionConfig[];
  activeSection: SkillScoreDimension;
  onSectionChange: (key: SkillScoreDimension) => void;
}

export const WorkforceProfileSkillsHub = ({sections, activeSection, onSectionChange}: Props) => {
  const availableSections = sections.filter((section) => section.available);
  const active = availableSections.find((section) => section.key === activeSection) ?? availableSections[0];

  if (!active) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0,
          borderBottom: `1px solid ${orangeColors[3]}`,
          mb: 2,
          mx: -1,
        }}
      >
        {availableSections.map((section) => (
          <ProfileTabButton
            key={section.key}
            label={section.label}
            active={active.key === section.key}
            onClick={() => onSectionChange(section.key)}
          />
        ))}
      </Box>

      {active.rows.length > 0 ? (
        <WorkforceProfileAlignmentItemsOverview
          title={active.title}
          rows={active.rows}
          itemKind={active.key}
          sx={{mb: 0}}
          newlyMetKeys={active.newlyMetKeys}
          newlyMetNote={active.newlyMetNote}
        />
      ) : (
        <Box sx={{px: 0.5, py: 0.5}}>
          <Typography variant='subtitle2' sx={{fontWeight: 600, mb: 1.5}}>
            {active.title}
          </Typography>
          {active.emptyExplanation && (
            <Typography variant='body2' color='text.secondary' sx={{lineHeight: 1.65}}>
              {active.emptyExplanation}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
